/**
 * Spec 105 — Persona Builder. Typed client for brand-scoped persona routes.
 *
 *   POST   /api/v1/brands/:brandId/personas
 *   GET    /api/v1/brands/:brandId/personas
 *   GET    /api/v1/brands/:brandId/personas/:personaId
 *   POST   /api/v1/brands/:brandId/personas/:personaId/versions
 *   POST   /api/v1/brands/:brandId/personas/:personaId/deactivate
 *   POST   /api/v1/brands/:brandId/personas/:personaId/reactivate
 */
import { apiFetch, apiGet } from '../lib/api.js';
import { apiPost } from '../lib/api-mutations.js';

export type PersonaType = 'commercial' | 'informational';
export type PersonaConfidence =
  | 'survey_grounded'
  | 'research_informed'
  | 'hypothesis';
export type PersonaDimensionType =
  | 'priority'
  | 'wellbeing'
  | 'trust_signal'
  | 'anti_signal'
  | 'query_disposition';

export const PERSONA_DIMENSION_TYPES: PersonaDimensionType[] = [
  'priority',
  'wellbeing',
  'trust_signal',
  'anti_signal',
  'query_disposition',
];

export const DIMENSION_LABELS: Record<PersonaDimensionType, string> = {
  priority: 'Priorities',
  wellbeing: 'Wellbeing',
  trust_signal: 'Trust signals',
  anti_signal: 'Anti-signals',
  query_disposition: 'Query disposition',
};

/**
 * Spec 110 — one-line helper copy per dimension family, rendered as persistent
 * muted sub-label text under each family heading in the persona form. Typed as a
 * Record over the closed PersonaDimensionType union so adding a future dimension
 * type fails to compile until its help copy is supplied (exhaustiveness guard).
 */
export const DIMENSION_HELP: Record<PersonaDimensionType, string> = {
  priority:
    "What this buyer cares about most when choosing. Does the brand lead with and clearly address these? (e.g. 'total cost of ownership', 'implementation speed')",
  trust_signal:
    'The evidence types this persona needs to believe a claim — proof points, credentials, or source types they trust (e.g. peer reviews, certifications, named case studies). Page evaluation checks whether the page itself provides these.',
  anti_signal:
    "Framing or terminology that alienates this persona and makes the brand read as 'not for me' (e.g. consumer-friendly language to a procurement buyer). Listing these lets evaluation penalize pages that use them.",
  wellbeing:
    'For informational personas: the emotional / wellbeing needs behind the question (e.g. reassurance, reduced anxiety). Scored separately from priorities.',
  query_disposition:
    'How this persona tends to ask — the phrasing, tone, and intent of their questions. Shapes the prompts we test; limited effect on page scoring.',
};

export interface PersonaDimension {
  id: string;
  dimension_type: PersonaDimensionType;
  label: string;
  weight: number;
  detail: string | null;
  sort_order: number;
}

export interface Persona {
  id: string;
  persona_id: string;
  version: number;
  is_current_version: boolean;
  brand_id: string;
  persona_type: PersonaType;
  confidence: PersonaConfidence;
  name: string;
  description: string | null;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  superseded_at: string | null;
  dimensions: PersonaDimension[];
}

export interface PersonaDimensionInput {
  dimension_type: PersonaDimensionType;
  label: string;
  weight?: number;
  detail?: string | null;
  sort_order?: number;
}

export interface PersonaCreateInput {
  name: string;
  description?: string | null;
  persona_type: PersonaType;
  confidence: PersonaConfidence;
  dimensions: PersonaDimensionInput[];
}

/**
 * The whole persona surface is behind the `feature.personas` flag, and with it
 * off every route answers 404 "Persona surface is disabled" (routes/personas.ts).
 * That is a deployment setting, not a missing record, so callers get their own
 * error type and can say so instead of printing "API error: 404".
 */
export class PersonaSurfaceDisabledError extends Error {
  constructor() {
    super('Persona surface is disabled');
    this.name = 'PersonaSurfaceDisabledError';
  }
}

export async function listPersonas(
  brandId: string,
  includeInactive = false,
): Promise<Persona[]> {
  const qs = includeInactive ? '?includeInactive=true' : '';
  const raw = await apiFetch(`/api/v1/brands/${brandId}/personas${qs}`);
  if (raw.status === 404) {
    const body = (await raw.json().catch(() => ({}))) as { message?: string };
    if (body.message === 'Persona surface is disabled') throw new PersonaSurfaceDisabledError();
  }
  if (!raw.ok) {
    const err = new Error(`API error: ${raw.status} ${raw.statusText}`);
    (err as Error & { status?: number }).status = raw.status;
    throw err;
  }
  const res = (await raw.json()) as { data: Persona[] };
  return Array.isArray(res.data) ? res.data : [];
}

export async function getPersona(
  brandId: string,
  personaId: string,
  version?: number,
): Promise<Persona> {
  const qs = version !== undefined ? `?version=${version}` : '';
  const res = await apiGet<{ data: Persona }>(
    `/api/v1/brands/${brandId}/personas/${personaId}${qs}`,
  );
  return res.data;
}

export async function createPersona(
  brandId: string,
  input: PersonaCreateInput,
): Promise<Persona> {
  const res = await apiPost<{ data: Persona }>(
    `/api/v1/brands/${brandId}/personas`,
    input,
  );
  return res.data;
}

export async function createPersonaVersion(
  brandId: string,
  personaId: string,
  input: PersonaCreateInput,
  baseVersion: number,
): Promise<Persona> {
  const res = await apiPost<{ data: Persona }>(
    `/api/v1/brands/${brandId}/personas/${personaId}/versions`,
    { ...input, base_version: baseVersion },
  );
  return res.data;
}

export async function deactivatePersona(
  brandId: string,
  personaId: string,
): Promise<Persona> {
  const res = await apiPost<{ data: Persona }>(
    `/api/v1/brands/${brandId}/personas/${personaId}/deactivate`,
    {},
  );
  return res.data;
}

export async function reactivatePersona(
  brandId: string,
  personaId: string,
): Promise<Persona> {
  const res = await apiPost<{ data: Persona }>(
    `/api/v1/brands/${brandId}/personas/${personaId}/reactivate`,
    {},
  );
  return res.data;
}

/* ------------------------------------------------------------------ */
/* Spec 111 — Dimension LLM-assist ("Suggest dimensions")             */
/* ------------------------------------------------------------------ */

/** One drafted dimension row (hypothesis-grade; editable; not yet saved). */
export interface PersonaDimensionSuggestion {
  label: string;
  weight: number;
  detail: string;
}

/** Spec 212 — which branch of the server's suggester produced a result. */
export type PersonaSuggestionSource = 'model' | 'fallback';

/** Spec 243 — why the template was returned. See the server type for the four cases. */
export type PersonaFallbackReason =
  | 'not_configured'
  | 'provider_unavailable'
  | 'call_failed'
  | 'unusable_response';

export type PersonaSuggestionFamily =
  | 'priority'
  | 'trust_signal'
  | 'anti_signal'
  | 'wellbeing'
  | 'query_disposition';

/** Grouped suggestion result. `wellbeing` is empty for commercial personas. */
export interface PersonaSuggestionResult {
  /**
   * Spec 212 — `'fallback'` means these are generic template rows, not a reading
   * of this persona's description. Optional so an older server response (which
   * carries neither field) is treated as unknown rather than as a live result.
   */
  source?: PersonaSuggestionSource;
  /** Families filled from the template even when `source` is `'model'`. */
  placeholder_families?: PersonaSuggestionFamily[];
  /** Spec 243 — why, when `source` is `'fallback'`. Optional: an older server sends neither. */
  fallback_reason?: PersonaFallbackReason | null;
  priority: PersonaDimensionSuggestion[];
  trust_signal: PersonaDimensionSuggestion[];
  anti_signal: PersonaDimensionSuggestion[];
  wellbeing: PersonaDimensionSuggestion[];
  query_disposition: PersonaDimensionSuggestion[];
}

export interface PersonaSuggestionInput {
  name: string;
  description: string;
  persona_type: PersonaType;
}

/**
 * Draft candidate dimensions for a persona from its name + description + type.
 * Persists nothing server-side and never affects the persona's confidence —
 * the caller merges these as editable rows into the form (spec 111).
 */
export async function suggestPersonaDimensions(
  brandId: string,
  input: PersonaSuggestionInput,
): Promise<PersonaSuggestionResult> {
  const res = await apiPost<{ data: PersonaSuggestionResult }>(
    `/api/v1/brands/${brandId}/personas/suggest-dimensions`,
    input,
  );
  return res.data;
}
